import { SizerSelectionController } from './sizerSelectionController';

/**
 * The ONE `SizerSelectionController` for the whole SPA — the React equivalent of
 * `header.js:203-215`, which constructs `new SizerPopup(...)` once per page load
 * on every page that has a logged-in user.
 *
 * ★ WHY A SINGLETON AND NOT A PER-PAGE INSTANCE.
 *
 * Phase 4e scoped the popup to `/sizer/` — `SizerPage` built its own controller
 * and handed the markup to `GlobalHeader` through a slot. That was a deliberate
 * narrowing (see the SCOPE note in `SizerSelectionPopup.tsx`) and it cost the
 * three behaviours QA reported: the popup did not follow the user onto the AHU
 * board / dashboard / project board, it could not be restored there from its
 * floating icon, and nothing on those pages listened for the `storage` events a
 * `/sizer/` tab fires. All three are one gap — the popup has to exist wherever
 * the header exists, which is what legacy gets for free by owning it.
 *
 * `GlobalHeader` now renders it on every board, so the instance has to outlive
 * any single page: navigating Sizer → AHU unmounts `SizerPage` (and its
 * `GlobalHeader`) and mounts the AHU board's, and an OPENED popup must survive
 * that the way it survives a legacy full-page navigation. A module singleton is
 * the SPA's version of "once per page load"; `destroy()` is therefore never
 * called, which is also why the controller's `destroyed` guard stays harmless.
 *
 * COST ON PAGES THAT NEVER SIZE — none worth avoiding, and legacy pays the same.
 * `initialize()` seeds `sizer_popup_fields_status` and calls `updateProjects`,
 * whose loaders (`sizerPopupApi.ts:201, :214`) early-return unless the
 * `sizer_popup` key exists. No `sizer_popup` ⇒ no request, and
 * `restoreFromStorage` finds no state to restore, so the header stays inert
 * until a `/sizer/` tab actually puts something there.
 */
let controller: SizerSelectionController | null = null;

export function getSizerSelectionController(): SizerSelectionController {
  if (!controller) controller = new SizerSelectionController();
  return controller;
}
